import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, Upload, Loader2, Save, Store, Hash, Tag, MessageSquare, 
  Phone, MessageCircle, Mail, Globe, Image, FileImage, MapPin,
  User, BarChart3, HelpCircle, Send, Eye, EyeOff, KeyRound, Trash2,
  CreditCard, Calendar, CheckCircle, XCircle, Clock, AlertTriangle, ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useAuth } from '../contexts/AuthContext';
import { trackStallUpdated, trackDashboardOpened, trackBackendActivity, getStoredEvents, getLinkClickAnalytics } from '../lib/analytics';
import { getImageUrl, getThumbnailUrl, bustImageCache } from '../lib/imageUtils';
import { useAutoLogout } from '../hooks/useAutoLogout';
import { toast } from 'sonner';
import { VendorAnalyticsTab, VendorSupportTab, VendorUserTab, VendorPaymentTab, VendorStallTab } from './vendor';
import StallModal from './StallModal';

const VendorDashboard = ({ isOpen, onClose }) => {
  const { token, user, setUser, logout } = useAuth();
  const [stall, setStall] = useState(null);
  const [hasStall, setHasStall] = useState(true); // Track if vendor has a stall
  const [categories, setCategories] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [marketTypeMap, setMarketTypeMap] = useState({});
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [marketLocations, setMarketLocations] = useState({});

  // Determine vendor type from user
  const vendorType = user?.vendor_type || 'market';
  const vendorTypes = user?.vendor_types && user.vendor_types.length > 0
    ? user.vendor_types
    : [vendorType];
  const pendingTypeRequests = user?.pending_type_requests || [];
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, banner: false });
  const [selectedFiles, setSelectedFiles] = useState({ logo: null, banner: null });
  const [showPreview, setShowPreview] = useState(false);

  // Auto logout after 10 minutes of inactivity
  const handleAutoLogout = useCallback(() => {
    logout();
    onClose();
  }, [logout, onClose]);
  
  useAutoLogout(10, handleAutoLogout, isOpen);

  // User profile state
  const [showPassword, setShowPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Account editing state
  const [accountData, setAccountData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    phone: ''
  });
  const [savingAccount, setSavingAccount] = useState(false);

  // Subscription state
  const [subscription, setSubscription] = useState({
    plan: 'none', // 'none', 'monthly', 'yearly'
    status: 'inactive', // 'inactive', 'active', 'cancelled', 'pending'
    nextBillingDate: null,
    startDate: null,
    daysRemaining: null // Days until trial/subscription expires
  });
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [processingPayment, setProcessingPayment] = useState(false);

  // Support state
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [sendingSupport, setSendingSupport] = useState(false);

  // Analytics state
  const [stallAnalytics, setStallAnalytics] = useState(null);
  const [linkClickAnalytics, setLinkClickAnalytics] = useState({
    contactDetails: [],
    socialMedia: [],
    totalDirections: { total: 0, today: 0, last7Days: 0, last30Days: 0 },
    directionsClicks: []
  });

  // New stall creation state (for vendors without a stall)
  const [newStall, setNewStall] = useState({
    stallName: '',
    category: '',
    slogan: '',
    about: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    socialMedia: { facebook: '', instagram: '', tiktok: '', twitter: '', linkedin: '' },
    status: 'draft'
  });
  const [newStallFiles, setNewStallFiles] = useState({ logo: null, banner: null });
  const [creatingStall, setCreatingStall] = useState(false);

  const API_URL = window.location.origin;

  // Initialize account data when user changes
  useEffect(() => {
    if (user) {
      // Handle "None" string as empty (data cleanup)
      const userPhone = user.phone && user.phone !== 'None' ? user.phone : '';
      setAccountData({
        firstName: user.first_name || user.firstName || user.name?.split(' ')[0] || '',
        lastName: user.last_name || user.lastName || user.name?.split(' ').slice(1).join(' ') || '',
        companyName: user.name || '',
        phone: userPhone
      });
      
      // Load subscription from user data
      const userSubscription = user.subscription || 'cash';
      const trialStartDate = user.trial_start_date;
      let subscriptionStatus = 'inactive';
      let daysRemaining = null;
      
      if (userSubscription === 'cash' || userSubscription === 'manual') {
        // Cash subscription - check next_billing_date for expiry
        if (user.next_billing_date) {
          const expiryDate = new Date(user.next_billing_date);
          const now = new Date();
          daysRemaining = Math.ceil((expiryDate - now) / (24 * 60 * 60 * 1000));
          subscriptionStatus = daysRemaining > 0 ? 'active' : 'expired';
        } else {
          subscriptionStatus = 'active';
        }
      } else if (userSubscription === 'trial') {
        // Check if trial is still valid (per-user duration; defaults to 7d)
        if (trialStartDate) {
          const durationDays = user.trial_duration_days || 7;
          const trialEnd = new Date(new Date(trialStartDate).getTime() + durationDays * 24 * 60 * 60 * 1000);
          const now = new Date();
          daysRemaining = Math.ceil((trialEnd - now) / (24 * 60 * 60 * 1000));
          subscriptionStatus = daysRemaining > 0 ? 'active' : 'expired';
        } else {
          subscriptionStatus = 'active'; // Legacy - assume active
        }
      } else if (userSubscription === 'monthly' || userSubscription === 'yearly') {
        subscriptionStatus = user.subscription_status || 'inactive';
        // Calculate days until next billing/cancellation
        if (user.next_billing_date) {
          const nextBilling = new Date(user.next_billing_date);
          const now = new Date();
          daysRemaining = Math.ceil((nextBilling - now) / (24 * 60 * 60 * 1000));
        }
        // For pending_cancellation, the subscription is still active until billing date
        if (subscriptionStatus === 'pending_cancellation') {
          // Keep daysRemaining as calculated - this shows days until cancellation takes effect
        }
      }
      
      setSubscription({
        plan: userSubscription,
        status: subscriptionStatus,
        nextBillingDate: user.next_billing_date || null,
        startDate: trialStartDate || user.subscription_start_date || null,
        daysRemaining: daysRemaining,
        cancelledAt: user.subscription_cancelled_at || null
      });
    }
  }, [user]);

  // Update account phone from stall if user phone is empty
  useEffect(() => {
    if (stall && (!accountData.phone || accountData.phone === 'None') && stall.phone && stall.phone !== 'None') {
      setAccountData(prev => ({
        ...prev,
        phone: stall.phone
      }));
    }
  }, [stall, accountData.phone]);

  useEffect(() => {
    if (isOpen) {
      fetchStall();
      fetchCategories();
      fetchTags();
      fetchMarkets();
      loadStallAnalytics();
      trackDashboardOpened('vendor');
    }

    const handleCategoriesUpdate = () => {
      fetchCategories();
      fetchMarkets();
    };
    
    const handleTagsUpdate = () => {
      fetchTags();
    };
    
    window.addEventListener('categoriesUpdated', handleCategoriesUpdate);
    window.addEventListener('tagsUpdated', handleTagsUpdate);
    
    return () => {
      window.removeEventListener('categoriesUpdated', handleCategoriesUpdate);
      window.removeEventListener('tagsUpdated', handleTagsUpdate);
    };
  }, [isOpen]);

  // Toasts are handled by sonner - no need for message state

  const loadStallAnalytics = async () => {
    const stallId = stall?._id || stall?.id;
    if (!stallId) return;
    
    try {
      // Fetch analytics from server
      const response = await fetch(`${API_URL}/api/analytics/stall/${stallId}`);
      if (response.ok) {
        const data = await response.json();
        setStallAnalytics({
          totalViews: data.views?.total || 0,
          todayViews: data.views?.today || 0,
          last7DaysViews: data.views?.last7Days || 0,
          last30DaysViews: data.views?.last30Days || 0,
          recentViews: data.recentEvents || []
        });
        
        // Transform link clicks to the format expected by the component
        const serverLinkClicks = data.linkClicks || {};
        const transformedLinkClicks = {
          contactDetails: [
            { type: 'phone', icon: 'Phone', label: 'Phone Calls', ...serverLinkClicks.phone },
            { type: 'whatsapp', icon: 'MessageCircle', label: 'WhatsApp', ...serverLinkClicks.whatsapp },
            { type: 'email', icon: 'Mail', label: 'Email', ...serverLinkClicks.email },
            { type: 'website', icon: 'Globe', label: 'Website', ...serverLinkClicks.website }
          ],
          socialMedia: [
            { type: 'facebook', icon: 'Facebook', label: 'Facebook', ...serverLinkClicks.facebook },
            { type: 'instagram', icon: 'Instagram', label: 'Instagram', ...serverLinkClicks.instagram },
            { type: 'tiktok', icon: 'Video', label: 'TikTok', ...serverLinkClicks.tiktok },
            { type: 'twitter', icon: 'Twitter', label: 'Twitter/X', ...serverLinkClicks.twitter },
            { type: 'linkedin', icon: 'Linkedin', label: 'LinkedIn', ...serverLinkClicks.linkedin }
          ],
          totalDirections: serverLinkClicks.directions || { total: 0, today: 0, last7Days: 0, last30Days: 0 },
          directionsClicks: [],
          engagement: [
            { type: 'share', label: 'Share Stall', ...serverLinkClicks.share },
            { type: 'save_contact', label: 'Save Contact', ...serverLinkClicks.save_contact }
          ]
        };
        setLinkClickAnalytics(transformedLinkClicks);
      } else {
        // Fallback to empty stats
        setStallAnalytics({
          totalViews: 0,
          todayViews: 0,
          last7DaysViews: 0,
          last30DaysViews: 0,
          recentViews: []
        });
        setLinkClickAnalytics({
          contactDetails: [],
          socialMedia: [],
          totalDirections: { total: 0, today: 0, last7Days: 0, last30Days: 0 },
          directionsClicks: [],
          engagement: []
        });
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
      // Fallback to empty stats
      setStallAnalytics({
        totalViews: 0,
        todayViews: 0,
        last7DaysViews: 0,
        last30DaysViews: 0,
        recentViews: []
      });
      setLinkClickAnalytics({
        contactDetails: [],
        socialMedia: [],
        totalDirections: { total: 0, today: 0, last7Days: 0, last30Days: 0 },
        directionsClicks: [],
        engagement: []
      });
    }
  };

  useEffect(() => {
    if (stall) {
      loadStallAnalytics();
    }
  }, [stall]);

  const fetchStall = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vendor/stall`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        
        // Check if vendor has a stall
        if (data.has_stall === false) {
          setHasStall(false);
          setStall(null);
          // Set empty analytics so the analytics tab doesn't show infinite spinner
          setStallAnalytics({ totalViews: 0, todayViews: 0, last7DaysViews: 0, last30DaysViews: 0, recentViews: [] });
          setLoading(false);
          return;
        }
        
        setHasStall(true);
        setStall(data);
        
        // Load from locations array (multi-market) if available, else fallback to legacy single market
        if (data.locations && data.locations.length > 0) {
          const markets = data.locations.map(loc => loc.market);
          setSelectedMarkets(markets);
          const locationsMap = {};
          data.locations.forEach(loc => {
            locationsMap[loc.market] = {
              stallNumber: loc.stallNumber || '',
              lat: loc.lat || loc.latitude || '',
              lng: loc.lng || loc.longitude || ''
            };
          });
          setMarketLocations(locationsMap);
        } else if (data.market) {
          // Fallback to legacy single market field
          setSelectedMarkets([data.market]);
          setMarketLocations({
            [data.market]: {
              stallNumber: data.stallNumber || '',
              lat: data.location?.lat || '',
              lng: data.location?.lng || ''
            }
          });
        }
        
        if (data.tags && data.tags.length > 0) {
          setSelectedTags(data.tags);
        }
      } else {
        // Handle non-200 responses (403 unapproved, 401 auth error, etc.)
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || 'Failed to load stall data';
        toast.error(errorMsg);
        setHasStall(false);
        setStall(null);
        // Set empty analytics so analytics tab doesn't show infinite spinner
        setStallAnalytics({ totalViews: 0, todayViews: 0, last7DaysViews: 0, last30DaysViews: 0, recentViews: [] });
      }
    } catch (error) {
      toast.error('Failed to load stall data');
      setHasStall(false);
      setStall(null);
      setStallAnalytics({ totalViews: 0, todayViews: 0, last7DaysViews: 0, last30DaysViews: 0, recentViews: [] });
    } finally {
      setLoading(false);
    }
  };

  // Create new stall (for vendors without a stall)
  const handleCreateStall = async () => {
    // Validation
    if (!newStall.stallName) {
      toast.error('Please enter a company/stall name');
      return;
    }
    if (!newStall.category) {
      toast.error('Please select a category');
      return;
    }
    if (!newStall.slogan) {
      toast.error('Please enter a slogan');
      return;
    }
    if (!newStall.about) {
      toast.error('Please enter an about description');
      return;
    }
    if (!newStall.phone) {
      toast.error('Please enter a phone number');
      return;
    }
    if (!newStall.email) {
      toast.error('Please enter an email');
      return;
    }
    if (selectedMarkets.length === 0) {
      toast.error('Please select at least one market');
      return;
    }
    
    // Validate market locations - stall number and GPS coordinates
    for (const market of selectedMarkets) {
      const loc = marketLocations[market];
      if (!loc?.stallNumber) {
        toast.error(`Please enter a stall number for ${market}`);
        return;
      }
      if (!loc?.lat || !loc?.lng) {
        toast.error(`Please enter GPS coordinates (Latitude & Longitude) for ${market}`);
        return;
      }
      const lat = parseFloat(loc.lat);
      const lng = parseFloat(loc.lng);
      if (isNaN(lat) || isNaN(lng)) {
        toast.error(`Please enter valid numeric GPS coordinates for ${market}`);
        return;
      }
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        toast.error(`Please enter valid GPS coordinates for ${market} (Latitude: -90 to 90, Longitude: -180 to 180)`);
        return;
      }
    }

    setCreatingStall(true);

    try {
      // Build locations array — each entry tagged with its type (market/expo/festival)
      // so the DB can distinguish between the three location kinds and downstream
      // display code never accidentally defaults an expo/festival stall to a market.
      const locations = selectedMarkets.map(market => ({
        market,
        type: marketTypeMap[market] || 'market',
        stallNumber: marketLocations[market]?.stallNumber || '',
        lat: marketLocations[market]?.lat ? parseFloat(marketLocations[market].lat) : null,
        lng: marketLocations[market]?.lng ? parseFloat(marketLocations[market].lng) : null
      }));

      // The root "market" field is the vendor's primary/first location.
      // Prefer a location matching the vendor's active primary type so expo/festival
      // vendors don't get labelled with a legacy market entry.
      const primaryType = vendorTypes[0] || 'market';
      const preferred = locations.find(l => l.type === primaryType) || locations[0];
      const firstMarket = preferred?.market || selectedMarkets[0];
      
      const stallData = {
        stallName: newStall.stallName,
        stallNumber: marketLocations[firstMarket]?.stallNumber || '',
        market: firstMarket,
        category: newStall.category,
        slogan: newStall.slogan,
        about: newStall.about,
        phone: newStall.phone,
        whatsapp: newStall.whatsapp,
        email: newStall.email,
        website: newStall.website,
        socialMedia: newStall.socialMedia,
        tags: selectedTags,
        location: {
          lat: marketLocations[firstMarket]?.lat ? parseFloat(marketLocations[firstMarket].lat) : null,
          lng: marketLocations[firstMarket]?.lng ? parseFloat(marketLocations[firstMarket].lng) : null
        },
        locations: locations,
        status: newStall.status
      };

      const response = await fetch(`${API_URL}/api/vendor/stall/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(stallData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create stall');
      }

      const result = await response.json();
      const stallId = result.stall_id;

      // Upload logo if provided
      if (newStallFiles.logo) {
        const logoFormData = new FormData();
        logoFormData.append('file', newStallFiles.logo);
        await fetch(`${API_URL}/api/vendor/stall/upload-logo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: logoFormData,
        });
      }

      // Upload banner if provided
      if (newStallFiles.banner) {
        const bannerFormData = new FormData();
        bannerFormData.append('file', newStallFiles.banner);
        await fetch(`${API_URL}/api/vendor/stall/upload-banner`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: bannerFormData,
        });
      }

      toast.success('Stall created successfully!');
      setHasStall(true);
      
      // Reload stall data
      fetchStall();
      
    } catch (error) {
      toast.error(error.message || 'Failed to create stall');
    } finally {
      setCreatingStall(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories/tags`);
      if (response.ok) {
        const data = await response.json();
        setAvailableTags(data);
      }
    } catch (error) {
      console.error('Failed to fetch tags');
    }
  };

  const fetchMarkets = async () => {
    try {
      // Fetch all three location lists so the vendor can select locations across
      // all types they have (or later gain) access to.
      const [mktRes, expRes, festRes] = await Promise.all([
        fetch(`${API_URL}/api/categories/markets`),
        fetch(`${API_URL}/api/categories/expos`),
        fetch(`${API_URL}/api/categories/festivals`),
      ]);
      const [mkts, expos, fests] = await Promise.all([
        mktRes.ok ? mktRes.json() : [],
        expRes.ok ? expRes.json() : [],
        festRes.ok ? festRes.json() : [],
      ]);

      // Only include lists for types the vendor currently has access to.
      const combined = [];
      const typeMap = {};
      if (vendorTypes.includes('market')) {
        mkts.forEach(name => { combined.push(name); typeMap[name] = 'market'; });
      }
      if (vendorTypes.includes('expo')) {
        expos.forEach(name => { combined.push(name); typeMap[name] = 'expo'; });
      }
      if (vendorTypes.includes('festival')) {
        fests.forEach(name => { combined.push(name); typeMap[name] = 'festival'; });
      }
      // De-dupe (in case a name appears in more than one collection)
      setMarkets([...new Set(combined)]);
      setMarketTypeMap(typeMap);
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const handleRequestTypeAccess = async (typeToRequest) => {
    try {
      const res = await fetch(`${API_URL}/api/vendor/request-type-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type: typeToRequest }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || 'Failed to submit access request');
        return;
      }
      alert(`Access request for ${typeToRequest} submitted. An admin will review it shortly.`);
      // Refresh user (best-effort — user's pending_type_requests will update on next login/me)
      if (user && !user.pending_type_requests?.includes(typeToRequest)) {
        user.pending_type_requests = [...(user.pending_type_requests || []), typeToRequest];
      }
    } catch (e) {
      alert('Network error submitting request');
    }
  };

  const handleMarketToggle = (market) => {
    if (selectedMarkets.includes(market)) {
      setSelectedMarkets(selectedMarkets.filter(m => m !== market));
      const newLocations = { ...marketLocations };
      delete newLocations[market];
      setMarketLocations(newLocations);
    } else {
      setSelectedMarkets([...selectedMarkets, market]);
      setMarketLocations({
        ...marketLocations,
        [market]: { stallNumber: '', lat: '', lng: '' }
      });
    }
  };

  const updateMarketLocation = (market, field, value) => {
    setMarketLocations({
      ...marketLocations,
      [market]: {
        ...marketLocations[market],
        [field]: value
      }
    });
  };

  const handleTagToggle = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else if (selectedTags.length < 10) {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);

    if (!stall?.logo) {
      toast.error('Please upload a logo image before saving.');
      setSaving(false);
      return;
    }
    if (!stall?.banner) {
      toast.error('Please upload a banner image before saving.');
      setSaving(false);
      return;
    }
    if (!stall?.slogan || stall.slogan.trim() === '') {
      toast.error('Please enter a slogan for your stall.');
      setSaving(false);
      return;
    }
    if (!stall?.about || stall.about.trim() === '') {
      toast.error('Please enter an about/description for your stall.');
      setSaving(false);
      return;
    }
    
    // Validate market selection
    if (!selectedMarkets || selectedMarkets.length === 0) {
      toast.error('Please select at least one market.');
      setSaving(false);
      return;
    }
    
    // Validate location coordinates for the primary market
    const primaryMarket = selectedMarkets[0];
    const primaryLocation = marketLocations[primaryMarket];
    if (!primaryLocation?.lat || !primaryLocation?.lng) {
      toast.error(`Please enter GPS coordinates (Latitude & Longitude) for ${primaryMarket}.`);
      setSaving(false);
      return;
    }
    
    // Validate that lat/lng are valid numbers
    const lat = parseFloat(primaryLocation.lat);
    const lng = parseFloat(primaryLocation.lng);
    if (isNaN(lat) || isNaN(lng)) {
      toast.error('Please enter valid numeric values for Latitude and Longitude.');
      setSaving(false);
      return;
    }
    
    // Validate lat/lng ranges (South Africa is roughly -22 to -35 lat, 16 to 33 lng)
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      toast.error('Please enter valid GPS coordinates (Latitude: -90 to 90, Longitude: -180 to 180).');
      setSaving(false);
      return;
    }

    try {
      const locations = selectedMarkets.map(market => ({
        market: market,
        type: marketTypeMap[market] || 'market',
        stallNumber: marketLocations[market]?.stallNumber || '',
        lat: parseFloat(marketLocations[market]?.lat) || null,
        lng: parseFloat(marketLocations[market]?.lng) || null
      }));

      // Prefer a location matching the vendor's primary type as the root "market" field
      const primaryType = vendorTypes[0] || 'market';
      const preferredLoc = locations.find(l => l.type === primaryType) || locations[0];
      const rootMarket = preferredLoc?.market || (selectedMarkets.length > 0 ? selectedMarkets[0] : null);

      const updateData = {
        stallName: stall.stallName,
        stallNumber: preferredLoc?.stallNumber || (selectedMarkets.length > 0 ? marketLocations[selectedMarkets[0]]?.stallNumber || '' : stall.stallNumber),
        market: rootMarket,
        category: stall.category,
        slogan: stall.slogan,
        about: stall.about,
        phone: stall.phone,
        whatsapp: stall.whatsapp,
        email: stall.email,
        website: stall.website,
        socialMedia: stall.socialMedia,
        tags: selectedTags,
        location: preferredLoc ? {
          lat: preferredLoc.lat,
          lng: preferredLoc.lng
        } : stall.location,
        locations: locations
      };

      const response = await fetch(`${API_URL}/api/vendor/stall`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        const updatedStall = await response.json();
        setStall(updatedStall);
        toast.success('Stall updated successfully!');
        trackStallUpdated(updatedStall._id || updatedStall.id, updatedStall.stallName, 'vendor');
        trackBackendActivity('stall_updated', '/api/vendor/stall', true, { stall_name: updatedStall.stallName });
      } else {
        throw new Error('Failed to update stall');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update stall');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (type, file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file format. Please use PNG, JPG, JPEG, or WEBP.');
      return;
    }

    // Validate file size (3MB max)
    const maxSize = 3 * 1024 * 1024; // 3MB in bytes
    if (file.size > maxSize) {
      toast.error('File size exceeds 3MB. Please upload a smaller image.');
      return;
    }

    setSelectedFiles(prev => ({ ...prev, [type]: file.name }));
    setUploading({ ...uploading, [type]: true });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/vendor/stall/upload-${type}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully!`);
        setSelectedFiles(prev => ({ ...prev, [type]: null }));
        fetchStall();
      } else {
        throw new Error(`Failed to upload ${type}`);
      }
    } catch (error) {
      toast.error(error.message || `Failed to upload ${type}`);
    } finally {
      setUploading({ ...uploading, [type]: false });
    }
  };

  const handleDeleteImage = async (type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/vendor/stall/${type}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully!`);
        fetchStall();
      } else {
        throw new Error(`Failed to delete ${type}`);
      }
    } catch (error) {
      toast.error(error.message || `Failed to delete ${type}`);
    }
  };

  const handleGalleryUpload = async (file) => {
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file format. Please use PNG, JPG, JPEG, or WEBP.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size exceeds 2MB. Please upload a smaller image.');
      return;
    }
    setUploading(prev => ({ ...prev, gallery: true }));
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API_URL}/api/vendor/stall/upload-gallery`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        toast.success('Gallery image uploaded!');
        fetchStall();
      } else {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to upload gallery image');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(prev => ({ ...prev, gallery: false }));
    }
  };

  const handleGalleryDelete = async (index) => {
    if (!window.confirm('Delete this gallery image?')) return;
    try {
      const response = await fetch(`${API_URL}/api/vendor/stall/gallery/${index}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success('Gallery image deleted!');
        fetchStall();
      } else {
        throw new Error('Failed to delete gallery image');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleStallStatusChange = async (newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/vendor/stall`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        setStall({ ...stall, status: newStatus });
        toast.success(newStatus === 'published' 
          ? 'Your stall is now published and visible to customers!' 
          : 'Your stall is now in draft mode and hidden from customers.');
      } else if (response.status === 402) {
        // Payment required - subscription issue
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Please subscribe to publish your stall. Go to User tab to manage your subscription.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update stall status');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update stall status');
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new !== passwordData.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: passwordData.current,
          new_password: passwordData.new
        }),
      });

      if (response.ok) {
        toast.success('Password changed. Please log in again with your new password.');
        setPasswordData({ current: '', new: '', confirm: '' });
        // Force a logout so the user can re-authenticate with the new password.
        // Slight delay so the toast is visible before the redirect/clear.
        setTimeout(() => {
          try { logout(); } catch (_) { /* noop */ }
          if (typeof onClose === 'function') onClose();
        }, 900);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveAccount = async () => {
    setSavingAccount(true);
    try {
      const response = await fetch(`${API_URL}/api/vendor/account`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          first_name: accountData.firstName,
          last_name: accountData.lastName,
          name: accountData.companyName,
          phone: accountData.phone
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Account information updated successfully!');
        
        // Update the user in context and localStorage
        if (data.user) {
          const updatedUser = { ...user, ...data.user };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          if (setUser) setUser(updatedUser);
        }
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update account');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingAccount(false);
    }
  };

  const handleSendSupport = async () => {
    if (!supportSubject.trim() || !supportMessage.trim()) {
      toast.error('Please fill in both subject and message');
      return;
    }

    setSendingSupport(true);
    try {
      // Send support email via backend
      const response = await fetch(`${API_URL}/api/support/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subject: supportSubject,
          message: supportMessage,
          from_email: user?.email || stall?.email,
          from_name: user?.name || stall?.stallName
        }),
      });

      if (response.ok) {
        toast.success('Support message sent successfully! We will get back to you soon.');
        setSupportSubject('');
        setSupportMessage('');
      } else {
        // Fallback: open email client
        const mailtoLink = `mailto:hello@wayo.co.za?subject=${encodeURIComponent(supportSubject)}&body=${encodeURIComponent(`From: ${user?.name || stall?.stallName} (${user?.email || stall?.email})\n\n${supportMessage}`)}`;
        window.location.href = mailtoLink;
        toast.success('Opening your email client...');
        setSupportSubject('');
        setSupportMessage('');
      }
    } catch (error) {
      // Fallback: open email client
      const mailtoLink = `mailto:hello@wayo.co.za?subject=${encodeURIComponent(supportSubject)}&body=${encodeURIComponent(`From: ${user?.name || stall?.stallName} (${user?.email || stall?.email})\n\n${supportMessage}`)}`;
      window.location.href = mailtoLink;
      toast.success('Opening your email client...');
      setSupportSubject('');
      setSupportMessage('');
    } finally {
      setSendingSupport(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-[#1a1a1a] sm:rounded-2xl w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[90vh] overflow-hidden flex flex-col sm:border-4 border-[#FFD12D] shadow-2xl">
        {/* Sticky Header */}
        <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b-2 border-[#FFD12D] px-3 py-3 sm:p-6 flex items-center justify-between sm:rounded-t-xl">
          <h2 className="text-base sm:text-2xl font-bold text-[#252525] dark:text-white">
            Vendor Dashboard
          </h2>
          <Button
            onClick={onClose}
            variant="ghost"
            className="w-8 h-8 sm:w-12 sm:h-12 p-0 rounded-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] shadow-lg transition-all duration-300"
          >
            <X className="w-4 h-4 sm:w-6 sm:h-6" />
          </Button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-3 py-4 sm:p-6 min-w-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#FFD12D]" />
              </div>
            ) : (
              <>
                {/* Payment-failed / draft banner — vendor can still log in and retry */}
                {(user?.status === 'suspended' || subscription.status === 'payment_failed') && (
                  <div className="mb-4 p-4 rounded-xl border-2 border-red-500 bg-red-50 dark:bg-red-950/30 flex items-start gap-3" data-testid="vendor-suspended-banner">
                    <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">!</span>
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-bold text-red-800 dark:text-red-300 mb-1">Payment failed — stall hidden from public</h4>
                      <p className="text-sm text-red-700 dark:text-red-400">
                        Your last {subscription.plan === 'yearly' ? 'yearly' : 'monthly'} subscription payment couldn't be processed, so your stall is currently in Draft (not visible to the public). Head over to the <strong>Subscription</strong> tab to update or retry your payment — your stall will go live again as soon as it clears.
                      </p>
                    </div>
                  </div>
                )}
                <Tabs defaultValue="user" className="w-full">
                {/* Sticky Tabs */}
                <div className="sticky top-0 z-10 bg-white dark:bg-[#1a1a1a] -mx-3 px-3 sm:-mx-6 sm:px-6 pb-3 sm:pb-4 border-b border-gray-200 dark:border-gray-700">
                  <TabsList className="flex w-full h-auto p-1 sm:p-2 bg-gray-100 dark:bg-[#252525] rounded-full overflow-x-auto gap-1 no-scrollbar">
                    <TabsTrigger value="user" className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs sm:text-sm py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full flex-1 min-w-0">
                      <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline">User</span>
                    </TabsTrigger>
                    <TabsTrigger value="stall" className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs sm:text-sm py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full flex-1 min-w-0">
                      <Store className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline">Stall</span>
                    </TabsTrigger>
                    <TabsTrigger value="analytics" className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs sm:text-sm py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full flex-1 min-w-0">
                      <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline">Analytics</span>
                    </TabsTrigger>
                    <TabsTrigger value="payment" className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs sm:text-sm py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full flex-1 min-w-0">
                      <CreditCard style={{ width: '14px', height: '14px', minWidth: '14px', minHeight: '14px' }} className="flex-shrink-0" />
                      <span className="hidden sm:inline">Subscription</span>
                    </TabsTrigger>
                    <TabsTrigger value="support" className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs sm:text-sm py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full flex-1 min-w-0">
                      <HelpCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline">Support</span>
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* User Tab */}
                <TabsContent value="user" className="space-y-6 mt-4">
                  <VendorUserTab
                    user={user}
                    stall={stall}
                    accountData={accountData}
                    setAccountData={setAccountData}
                    savingAccount={savingAccount}
                    handleSaveAccount={handleSaveAccount}
                    subscription={subscription}
                    handleStallStatusChange={handleStallStatusChange}
                    passwordData={passwordData}
                    setPasswordData={setPasswordData}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    changingPassword={changingPassword}
                    handleChangePassword={handleChangePassword}
                    marketLocations={marketLocations}
                  />
                </TabsContent>

              {/* Stall Tab */}
              <TabsContent value="stall" className="space-y-6">
                <VendorStallTab
                  stall={stall} setStall={setStall}
                  hasStall={hasStall}
                  categories={categories} markets={markets}
                  selectedMarkets={selectedMarkets} handleMarketToggle={handleMarketToggle}
                  marketLocations={marketLocations} setMarketLocations={setMarketLocations} updateMarketLocation={updateMarketLocation}
                  availableTags={availableTags} selectedTags={selectedTags} setSelectedTags={setSelectedTags} handleTagToggle={handleTagToggle}
                  saving={saving} uploading={uploading} selectedFiles={selectedFiles}
                  showPreview={showPreview} setShowPreview={setShowPreview}
                  handleImageUpload={handleImageUpload} handleDeleteImage={handleDeleteImage}
                  handleGalleryUpload={handleGalleryUpload} handleGalleryDelete={handleGalleryDelete}
                  handleCreateStall={handleCreateStall} handleUpdate={handleUpdate}
                  newStall={newStall} setNewStall={setNewStall} newStallFiles={newStallFiles} setNewStallFiles={setNewStallFiles}
                  vendorType={vendorType}
                  vendorTypes={vendorTypes}
                  marketTypeMap={marketTypeMap}
                  pendingTypeRequests={pendingTypeRequests}
                  onRequestTypeAccess={handleRequestTypeAccess}
                />
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics" className="space-y-6">
                <VendorAnalyticsTab 
                  stall={stall}
                  stallAnalytics={stallAnalytics}
                  linkClickAnalytics={linkClickAnalytics}
                />
              </TabsContent>

              {/* Payment Tab */}
              <TabsContent value="payment" className="space-y-6">
                <VendorPaymentTab
                  subscription={subscription}
                  setSubscription={setSubscription}
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                  processingPayment={processingPayment}
                  setProcessingPayment={setProcessingPayment}
                  API_URL={API_URL}
                  user={user}
                  vendorType={vendorType}
                />
              </TabsContent>

              {/* Support Tab */}
              <TabsContent value="support" className="space-y-6">
                <VendorSupportTab
                  supportSubject={supportSubject}
                  setSupportSubject={setSupportSubject}
                  supportMessage={supportMessage}
                  setSupportMessage={setSupportMessage}
                  sendingSupport={sendingSupport}
                  handleSendSupport={handleSendSupport}
                />
              </TabsContent>
            </Tabs>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Stall Preview Modal */}
      <StallModal 
        stall={stall}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
      />
    </div>
  );
};

export default VendorDashboard;
